import { useRef, useEffect } from "react";
import { gql, useQuery } from "@apollo/client";
import { View, Platform } from "react-native";
import { ActivityIndicator, Text } from "react-native-paper";
import SvgQRCode from "react-native-qrcode-svg";
import { cond, stubTrue } from "lodash";
import * as Brightness from "expo-brightness";

import { Modal, Chip } from "./";
import { ProfileQuery } from "../screens/HomeScreen/queries";
import { V3_CLIENT_NAME } from "./GraphqlProvider";
import PgaLogo from "../svgs/PgaLogo";

export const MEMBER_ID_CARD_QUERY = gql`
  query MemberIdCardQuery {
    me {
      id
      displayName
      firstName
      lastName
      memberClass: class
      certifiedProfessional
      specializedProfessional
      masterProfessional
      section {
        id
        name
      }
    }
  }
`;

const MemberIdCardData = () => {
  const { loading, data } = useQuery<ProfileQuery>(MEMBER_ID_CARD_QUERY, { context: { clientName: V3_CLIENT_NAME } });
  const {
    id,
    displayName,
    section,
    firstName,
    lastName,
    memberClass,
    certifiedProfessional,
    specializedProfessional,
    masterProfessional,
  } = data?.me || {};

  const currentBrightnessRef = useRef<number | null>(null);

  const handleBrightnessIncrease = async () => {
    const brightness = await Brightness.getBrightnessAsync();
    currentBrightnessRef.current = brightness;
    await Brightness.setBrightnessAsync(1);
  };

  useEffect(() => {
    if (Platform.OS === "web") return;

    handleBrightnessIncrease();

    return () => {
      if (currentBrightnessRef.current !== null && currentBrightnessRef.current !== undefined) {
        Brightness.setBrightnessAsync(currentBrightnessRef.current);
      }
    };
  }, []);

  if (loading && !data?.me) {
    return <ActivityIndicator className="flex-1 items-center justify-center my-6" testID="member-id-card-loader" />;
  }

  const displayCertificationLogo = cond([
    [() => !!masterProfessional, () => <PgaLogo width={90} />],
    [() => !!certifiedProfessional, () => <PgaLogo width={90} />],
    [() => !!specializedProfessional, () => <PgaLogo width={90} />],
    [stubTrue, () => <PgaLogo width={90} />],
  ]);

  return (
    <View
      className="my-6 lg:my-10 mx-6 lg:mx-12 border border-sky-light rounded-lg bg-sky-white shadow-sm"
      testID="MemberIdCardModal"
    >
      <View className="py-4 sm:py-6 lg:py-8 flex-1 items-center">
        <View className="mb-10 sm:mb-16 mt-2 sm:mt-3">{displayCertificationLogo()}</View>
        <SvgQRCode value={id} size={180} />
        <Text className="mt-2 sm:mt-4 mb-8 sm:mb-14 text-regular-normal font-500 text-ink-dark">Member ID: {id}</Text>
      </View>

      <View className="border-t border-sky-light items-start p-8">
        <Text className="text-title-4 sm:text-title-5 font-700 text-ink-darker">
          {displayName ?? `${firstName} ${lastName}`}
        </Text>
        <View className="mt-2">
          <Chip text="MEMBER" />
        </View>

        {memberClass && <Text className="text-small-tight font-400 text-ink-dark mt-2">Class {memberClass}</Text>}
        {section && <Text className="text-small-tight font-400 text-ink-dark mt-2">{section.name}</Text>}
        <Text className="text-small-tight font-400 text-ink-dark mt-2">Member ID: {id}</Text>
      </View>
    </View>
  );
};

export const MemberIdCardModal = ({ visible, setVisible }: { visible: boolean; setVisible(v: boolean): void }) => {
  return (
    <Modal
      {...{
        fullScreen: true,
        visible,
        onDismiss: () => setVisible(false),
        title: "PGA Member ID",
        hasBorder: false,
      }}
    >
      {visible && <MemberIdCardData />}
    </Modal>
  );
};
